import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { Modal } from '../../../components/ui/Modal.jsx';
import { Input } from '../../../components/ui/Input.jsx';
import { Select } from '../../../components/ui/Select.jsx';
import { Textarea } from '../../../components/ui/Textarea.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { TagInput } from '../../../components/ui/TagInput.jsx';
import { consultationsApi } from '../../../api/consultations.js';
import { ApiError } from '../../../api/client.js';
import { applyServerValidation } from '../../../lib/forms.js';
import { t } from '../../../lib/i18n.js';

import { ConsultationProductPicker } from './ConsultationProductPicker.jsx';

const TYPES = [
  { value: 'general', label: 'Generale' },
  { value: 'nutrition', label: 'Nutrizione' },
  { value: 'behaviour', label: 'Comportamento' },
  { value: 'health', label: 'Salute' },
  { value: 'other', label: 'Altro' }
];

const EMPTY = { petId: '', type: 'general', date: '', summary: '', recommendations: [], products: [], notes: '', followUpAt: '' };

export function ConsultationFormModal({ open, onClose, customerId, pets, initial }) {
  const editing = Boolean(initial && initial._id);
  const queryClient = useQueryClient();
  const { register, handleSubmit, reset, control, setError, formState: { errors } } = useForm({ defaultValues: EMPTY });

  useEffect(() => {
    if (!open) return;
    if (initial) {
      reset({
        petId: String(initial.petId?._id || initial.petId || ''),
        type: initial.type || 'general',
        date: initial.date ? new Date(initial.date).toISOString().slice(0, 10) : '',
        summary: initial.summary || '',
        recommendations: initial.recommendations || [],
        products: (initial.products || []).map((p) => ({ productId: p.productId?._id || p.productId, name: p.name, sku: p.sku, purchased: !!p.purchased })),
        notes: initial.notes || '',
        followUpAt: initial.followUpAt ? new Date(initial.followUpAt).toISOString().slice(0, 10) : ''
      });
    } else {
      reset(EMPTY);
    }
  }, [open, initial, reset]);

  const mutation = useMutation({
    mutationFn: (values) => {
      const payload = {
        type: values.type,
        date: values.date || undefined,
        summary: values.summary || undefined,
        notes: values.notes || undefined,
        recommendations: values.recommendations,
        products: (values.products || []).map((p) => ({ productId: p.productId, purchased: !!p.purchased })),
        followUpAt: values.followUpAt || null
      };
      const petId = values.petId || null;
      if (editing) return consultationsApi.update(initial._id, { ...payload, petId });
      return consultationsApi.create({ ...payload, customerId, petId });
    },
    onSuccess: () => {
      toast.success(editing ? 'Consulenza aggiornata.' : 'Consulenza registrata.');
      queryClient.invalidateQueries({ queryKey: ['customer', customerId, 'consultations'] });
      onClose();
    },
    onError: (err) => {
      const applied = applyServerValidation(err, setError);
      if (!applied) toast.error(err instanceof ApiError ? err.message : t('common.error'));
    }
  });

  return (
    <Modal
      open={open}
      onClose={mutation.isPending ? undefined : onClose}
      title={editing ? 'Modifica consulenza' : 'Nuova consulenza'}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>{t('common.cancel')}</Button>
          <Button form="consultation-form" type="submit" loading={mutation.isPending}>{t('common.save')}</Button>
        </>
      }
    >
      <form id="consultation-form" className="flex flex-col gap-4" onSubmit={handleSubmit(mutation.mutate)} noValidate>
        <section className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Select label="Animale" {...register('petId')}>
            <option value="">Generale (cliente)</option>
            {(pets || []).map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
          </Select>
          <Select label="Tipo" {...register('type')}>
            {TYPES.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
          </Select>
          <Input label="Data" type="date" {...register('date')} />
        </section>
        <Input label="Sintesi" error={errors.summary?.message} {...register('summary')} />
        <Controller
          control={control}
          name="recommendations"
          render={({ field }) => <TagInput label="Consigli (testo libero)" value={field.value} onChange={field.onChange} placeholder="Aggiungi e premi Invio" />}
        />
        <div>
          <p className="mb-2 text-sm font-medium text-ink">Prodotti associati</p>
          <Controller
            control={control}
            name="products"
            render={({ field }) => <ConsultationProductPicker value={field.value || []} onChange={field.onChange} />}
          />
        </div>
        <Textarea label="Note" rows={3} {...register('notes')} />
        <Input label="Follow-up il" type="date" hint="Data di un eventuale richiamo." {...register('followUpAt')} />
      </form>
    </Modal>
  );
}
